import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import MarcheFormModal, { ETAT_BADGE, getEtat, formatMontant, toInputDate } from './MarcheFormModal';

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
  },
  dialog: {
    background: '#fff', borderRadius: '8px', width: '680px', maxWidth: '95vw',
    maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    boxShadow: '0 8px 32px rgba(0,0,0,0.25)',
  },
  dialogHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '1.25rem 1.5rem', background: '#2c3e50', borderRadius: '8px 8px 0 0',
  },
  dialogTitle: { color: '#fff', fontSize: '1.1rem', fontWeight: 600, margin: 0 },
  closeBtn: { background: 'none', border: 'none', color: '#fff', fontSize: '1.5rem', cursor: 'pointer' },
  input: {
    width: '100%', padding: '0.6rem 0.75rem', border: '1px solid #ddd',
    borderRadius: '4px', fontSize: '0.9rem', boxSizing: 'border-box',
  },
  th: {
    cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap',
  },
  thInner: {
    display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
  },
  sortArrow: {
    fontSize: '0.7rem', opacity: 0.4, transition: 'opacity 0.15s',
  },
  sortArrowActive: {
    fontSize: '0.7rem', opacity: 1, color: '#3498db',
  },
};

const ALL_COLUMNS = [
  { key: 'entreprise_nom', label: 'Entité', default: true, sortField: 'entreprise_nom' },
  { key: 'codification', label: 'Codification', default: true, sortField: 'codification', locked: true },
  { key: 'client_nom', label: 'Client', default: true, sortField: 'client_nom' },
  { key: 'reference_client', label: 'Réf. client', default: true, sortField: 'reference_client' },
  { key: 'intitule_marche', label: 'Intitulé', default: true, sortField: 'intitule_marche' },
  { key: 'lot', label: 'Lot', default: true, sortField: 'lot' },
  { key: 'montant', label: 'Montant', default: true, sortField: 'montant' },
  { key: 'conditions_financieres', label: 'Modalité Prix', default: true, sortField: 'conditions_financieres' },
  { key: 'etat', label: 'État', default: true, sortField: 'etat' },
  { key: 'dlro', label: 'DLRO', default: true, sortField: 'dlro' },
  { key: 'notification', label: 'Notification', default: true, sortField: 'notification' },
];

const STORAGE_KEY = 'marche-columns-config';
const STORAGE_KEY_WIDTHS = 'marche-columns-widths';

// ─── En-tête triable ──────────────────────────────────────────────────────────
function SortTh({ label, field, sortField, sortDir, onSort, style, onResize }) {
  const active = sortField === field;
  return (
    <th style={{ ...S.th, ...style }} onClick={() => onSort(field)}>
      <span style={S.thInner}>
        {label}
        <span style={active ? S.sortArrowActive : S.sortArrow}>
          {active ? (sortDir === 'asc' ? '▲' : '▼') : '▲▼'}
        </span>
      </span>
      {onResize && (
        <span
          onMouseDown={onResize}
          onClick={e => e.stopPropagation()}
          style={{ position: 'absolute', right: 0, top: 0, bottom: 0, width: '5px',
            cursor: 'col-resize', background: 'transparent' }}
          onMouseEnter={e => { e.currentTarget.style.background = '#3498db'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'transparent'; }}
        />
      )}
    </th>
  );
}

// ─── Composant principal ──────────────────────────────────────────────────────
function MarcheList({ user }) {
  const navigate = useNavigate();
  const [marches, setMarches]   = useState([]);
  const [clients, setClients]   = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMarche, setEditMarche] = useState(null);
  const [dupForm, setDupForm]     = useState(null);
  const [showDupModal, setShowDupModal] = useState(false);
  const [dupSearch, setDupSearch] = useState('');

  const canDelete = user && user.role === 'Admin';

  // Tri
  const [sortField, setSortField] = useState('codification');
  const [sortDir, setSortDir]     = useState('asc');

  // Filtres par état (Perdu et Terminé masqués par défaut)
  const [activeFilters, setActiveFilters] = useState({
    'Non répondu': true,
    'En cours': true,
    'Perdu': false,
    'Gagné': true,
    'Terminé': false,
    'Declaration sans suite': false,
  });

  // Recherche
  const [search, setSearch] = useState('');

  // Configuration colonnes
  const [visibleCols, setVisibleCols] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) return JSON.parse(saved);
    } catch(e) {}
    return ALL_COLUMNS.filter(c => c.default).map(c => c.key);
  });

  const [colWidths, setColWidths] = useState(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY_WIDTHS);
      if (saved) return JSON.parse(saved);
    } catch(e) {}
    return {};
  });

  const [showColConfig, setShowColConfig] = useState(false);

  useEffect(() => { fetchMarches(); fetchClients(); fetchEntreprises(); }, []); // eslint-disable-line

  // Persistance colonnes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(visibleCols));
  }, [visibleCols]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY_WIDTHS, JSON.stringify(colWidths));
  }, [colWidths]);

  const fetchMarches = async () => {
    try {
      const r = await axios.get('/api/marches');
      setMarches(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchClients = async () => {
    try {
      const r = await axios.get('/api/clients');
      setClients(r.data);
    } catch(e) { console.error(e); }
  };

  const fetchEntreprises = async () => {
    try {
      const r = await axios.get('/api/entreprises');
      setEntreprises(r.data);
    } catch(e) { console.error(e); }
  };

  // ── Tri ──────────────────────────────────────────────────────────────────────
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  // Toggle colonne
  const toggleColumn = (key) => {
    setVisibleCols(prev =>
      prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
    );
  };

  // Resize colonnes
  const startResize = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const th = e.target.closest('th');
    const startWidth = th.offsetWidth;

    const onMouseMove = (moveE) => {
      const diff = moveE.clientX - startX;
      const newWidth = Math.max(60, startWidth + diff);
      setColWidths(prev => ({ ...prev, [colKey]: newWidth }));
    };

    const onMouseUp = () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };

  // Colonnes visibles
  const displayColumns = ALL_COLUMNS.filter(c => c.locked || visibleCols.includes(c.key));

  const sortedMarches = useMemo(() => {
    // Filtrer par état actif
    let filtered = marches.filter(m => activeFilters[getEtat(m)] !== false);
    if (search.trim()) {
      const s = search.toLowerCase();
      filtered = filtered.filter(m =>
        (m.codification || '').toLowerCase().includes(s) ||
        (m.entreprise_nom || '').toLowerCase().includes(s) ||
        (m.client_nom || '').toLowerCase().includes(s) ||
        (m.reference_client || '').toLowerCase().includes(s) ||
        (m.intitule_marche || '').toLowerCase().includes(s) ||
        (m.lot || '').toLowerCase().includes(s) ||
        getEtat(m).toLowerCase().includes(s)
      );
    }
    const sorted = [...filtered].sort((a, b) => {
      let valA, valB;

      switch (sortField) {
        case 'codification':
          valA = (a.codification || '').toLowerCase();
          valB = (b.codification || '').toLowerCase();
          break;
        case 'entreprise_nom':
          valA = (a.entreprise_nom || '').toLowerCase();
          valB = (b.entreprise_nom || '').toLowerCase();
          break;
        case 'client_nom':
          valA = (a.client_nom || '').toLowerCase();
          valB = (b.client_nom || '').toLowerCase();
          break;
        case 'reference_client':
          valA = (a.reference_client || '').toLowerCase();
          valB = (b.reference_client || '').toLowerCase();
          break;
        case 'intitule_marche':
          valA = (a.intitule_marche || '').toLowerCase();
          valB = (b.intitule_marche || '').toLowerCase();
          break;
        case 'lot':
          valA = (a.lot || '').toLowerCase();
          valB = (b.lot || '').toLowerCase();
          break;
        case 'montant':
          valA = parseFloat(a.montant) || 0;
          valB = parseFloat(b.montant) || 0;
          break;
        case 'conditions_financieres':
          valA = (a.conditions_financieres || '').toLowerCase();
          valB = (b.conditions_financieres || '').toLowerCase();
          break;
        case 'etat':
          valA = getEtat(a).toLowerCase();
          valB = getEtat(b).toLowerCase();
          break;
        case 'dlro':
          valA = a.dlro ? new Date(a.dlro).getTime() : 0;
          valB = b.dlro ? new Date(b.dlro).getTime() : 0;
          break;
        case 'notification':
          valA = a.notification ? new Date(a.notification).getTime() : 0;
          valB = b.notification ? new Date(b.notification).getTime() : 0;
          break;
        default:
          valA = ''; valB = '';
      }

      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [marches, sortField, sortDir, activeFilters, search]); // eslint-disable-line

  const openCreate = () => {
    setEditMarche(null);
    setDupForm(null);
    setShowModal(true);
  };

  // Dupliquer : selectionner un marche puis ouvrir le formulaire pre-rempli (sauf lot)
  const selectDuplicate = (m) => {
    setShowDupModal(false);
    setDupSearch('');
    setEditMarche(null); // creation, pas edition
    setDupForm({
      client_codification:  m.client_codification || '',
      reference_client:     m.reference_client || '',
      dlro:                 toInputDate(m.dlro),
      intitule_marche:      m.intitule_marche || '',
      lot:                  '', // lot vide pour la duplication
      montant:              m.montant || '',
      conditions_financieres: m.conditions_financieres || 'Non',
      etat:                 getEtat(m),
      notification:         toInputDate(m.notification),
      heure_depot:          m.heure_depot || '',
      entreprise_codification: m.entreprise_codification || '',
    });
    setShowModal(true);
  };

  const openEdit = (m) => {
    setEditMarche(m);
    setDupForm(null);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer le marché n°' + id + ' ?')) return;
    try {
      await axios.delete('/api/marches/' + id);
      fetchMarches();
    } catch(err) {
      alert(err.response?.data?.error || 'Erreur lors de la suppression.');
    }
  };

  const renderCell = (m, colKey) => {
    switch(colKey) {
      case 'entreprise_nom':
        return m.entreprise_nom || <em style={{ color: '#aaa' }}>—</em>;
      case 'codification':
        return <strong>{m.codification || '#' + m.reference_interne}</strong>;
      case 'client_nom':
        return m.client_nom || <em style={{ color: '#aaa' }}>—</em>;
      case 'reference_client':
        return m.reference_client || '—';
      case 'intitule_marche':
        return m.intitule_marche;
      case 'lot':
        return m.lot || '—';
      case 'montant':
        return m.montant ? formatMontant(m.montant) + ' €' : '—';
      case 'conditions_financieres':
        return m.conditions_financieres && m.conditions_financieres !== 'Non'
          ? <span className="badge" style={{ background: '#d1ecf1', color: '#0c5460' }}>{m.conditions_financieres}</span>
          : <span style={{ color: '#aaa' }}>Prix ferme</span>;
      case 'etat': {
        const etat = getEtat(m);
        const badgeStyle = ETAT_BADGE[etat] || ETAT_BADGE['En cours'];
        return <span style={{ ...badgeStyle, padding: '0.25rem 0.6rem', borderRadius: '12px', fontSize: '0.82rem', fontWeight: 600, whiteSpace: 'nowrap' }}>{etat}</span>;
      }
      case 'dlro':
        return m.dlro ? new Date(m.dlro).toLocaleDateString('fr-FR') : '—';
      case 'notification':
        return m.notification ? new Date(m.notification).toLocaleDateString('fr-FR') : '—';
      default:
        return '—';
    }
  };

  if (loading) return <div className="loading">Chargement…</div>;

  return (
    <div className="card">
      {/* ── En-tête ── */}
      <div className="card-header">
        <h1 className="card-title">Marchés</h1>
        <input
          type="text"
          placeholder="Rechercher..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ flex: '1', maxWidth: '300px', margin: '0 1rem', padding: '0.5rem 0.75rem', border: '1px solid #ddd', borderRadius: '4px', fontSize: '0.9rem' }}
        />
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="btn btn-primary" onClick={openCreate}>+ Nouveau marché</button>
          <button className="btn btn-secondary" onClick={() => { setDupSearch(''); setShowDupModal(true); }}>Dupliquer marché</button>
        </div>
      </div>

      {/* ── Filtres par état ── */}
      <div style={{ padding: '0 1.5rem 1rem', display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'center' }}>
        <span style={{ fontSize: '0.85rem', color: '#7f8c8d', marginRight: '0.5rem' }}>Filtrer :</span>
        {Object.entries(activeFilters).map(([etat, active]) => {
          const badge = ETAT_BADGE[etat] || {};
          return (
            <button key={etat}
              onClick={() => setActiveFilters(prev => ({ ...prev, [etat]: !prev[etat] }))}
              style={{
                padding: '0.3rem 0.75rem', borderRadius: '16px', fontSize: '0.82rem',
                fontWeight: 600, cursor: 'pointer', border: '2px solid transparent',
                background: active ? (badge.background || '#e2e3e5') : '#f8f9fa',
                color: active ? (badge.color || '#383d41') : '#adb5bd',
                opacity: active ? 1 : 0.6,
                transition: 'all 0.15s',
              }}>
              {etat}
            </button>
          );
        })}
        <button onClick={() => setShowColConfig(prev => !prev)}
          style={{ marginLeft: 'auto', padding: '0.3rem 0.6rem', borderRadius: '4px', fontSize: '0.82rem',
            fontWeight: 600, cursor: 'pointer', border: '1px solid #ddd', background: showColConfig ? '#3498db' : '#f8f9fa',
            color: showColConfig ? '#fff' : '#7f8c8d' }}
          title="Configurer les colonnes">
          ⚙ Colonnes
        </button>
      </div>

      {showColConfig && (
        <div style={{ padding: '0.5rem 1.5rem 1rem', background: '#f8f9fa', borderBottom: '1px solid #ecf0f1' }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {ALL_COLUMNS.filter(c => !c.locked).map(col => (
              <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', fontSize: '0.82rem',
                padding: '0.25rem 0.5rem', borderRadius: '4px', cursor: 'pointer',
                background: visibleCols.includes(col.key) ? '#d4edda' : '#f8d7da',
                color: visibleCols.includes(col.key) ? '#155724' : '#721c24' }}>
                <input type="checkbox" checked={visibleCols.includes(col.key)}
                  onChange={() => toggleColumn(col.key)} />
                {col.label}
              </label>
            ))}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#95a5a6', marginTop: '0.5rem' }}>
            Glissez le bord droit des en-têtes pour redimensionner les colonnes.
          </div>
        </div>
      )}

      {/* ── Tableau ── */}
      <div className="table-wrapper">
      <table style={{ tableLayout: 'auto', width: '100%' }}>
        <thead>
          <tr>
            {displayColumns.map(col => (
              <SortTh key={col.key} label={col.label} field={col.sortField}
                sortField={sortField} sortDir={sortDir} onSort={handleSort}
                style={{ width: colWidths[col.key] ? colWidths[col.key] + 'px' : undefined, position: 'relative' }}
                onResize={col.locked ? null : (e) => startResize(e, col.key)} />
            ))}
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {sortedMarches.length === 0 ? (
            <tr><td colSpan={displayColumns.length + 1} style={{ textAlign: 'center', padding: '2rem' }}>Aucun marché</td></tr>
          ) : sortedMarches.map((m, idx) => (
            <tr key={m.reference_interne} style={{ background: idx % 2 === 0 ? '#fff' : '#f8f9fa' }}>
              {displayColumns.map(col => (
                <td key={col.key} style={{
                  ...(col.key === 'montant' ? { textAlign: 'right', fontVariantNumeric: 'tabular-nums' } : {}),
                  ...(col.key === 'intitule_marche' ? { maxWidth: '200px' } : {}),
                  ...(colWidths[col.key] ? { width: colWidths[col.key] + 'px' } : {}),
                }}>
                  {renderCell(m, col.key)}
                </td>
              ))}
              <td>
                <div className="actions">
                  <button onClick={() => navigate('/marches/' + m.reference_interne)} className="btn btn-secondary" title="Détail" style={{ padding: '0.35rem 0.6rem', minWidth: '32px' }}>D</button>
                  <button onClick={() => openEdit(m)} className="btn btn-primary" title="Modifier" style={{ padding: '0.35rem 0.6rem', minWidth: '32px' }}>M</button>
                  {canDelete && <button onClick={() => handleDelete(m.reference_interne)} className="btn btn-danger" title="Supprimer" style={{ padding: '0.35rem 0.6rem', minWidth: '32px' }}>S</button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>

      {/* ── Modale création / modification ── */}
      {showModal && (
        <MarcheFormModal
          user={user}
          clients={clients}
          entreprises={entreprises}
          marche={editMarche}
          initialForm={dupForm}
          initialViewOnly={false}
          onClose={() => setShowModal(false)}
          onSaved={fetchMarches}
          onDelete={canDelete ? handleDelete : null}
        />
      )}

      {/* ── Modale duplication ── */}
      {showDupModal && (
        <div style={S.overlay} onClick={() => setShowDupModal(false)}>
          <div style={{ ...S.dialog, width: '560px' }} onClick={e => e.stopPropagation()}>
            <div style={{ ...S.dialogHeader, background: '#8e44ad' }}>
              <h2 style={S.dialogTitle}>Dupliquer un marché</h2>
              <button style={S.closeBtn} onClick={() => setShowDupModal(false)}>×</button>
            </div>
            <div style={{ padding: '1rem 1.5rem 0.5rem' }}>
              <input
                type="text"
                placeholder="Rechercher un marché (intitulé, codification, client, lot...)"
                value={dupSearch}
                onChange={e => setDupSearch(e.target.value)}
                style={{ ...S.input, marginBottom: '0.75rem' }}
                autoFocus
              />
            </div>
            <div style={{ padding: '0 1.5rem 1.5rem', maxHeight: '400px', overflowY: 'auto' }}>
              {(() => {
                const s = dupSearch.toLowerCase();
                const filtered = marches.filter(m =>
                  !s ||
                  (m.codification || '').toLowerCase().includes(s) ||
                  (m.intitule_marche || '').toLowerCase().includes(s) ||
                  (m.client_nom || '').toLowerCase().includes(s) ||
                  (m.reference_client || '').toLowerCase().includes(s) ||
                  (m.lot || '').toLowerCase().includes(s) ||
                  (m.entreprise_nom || '').toLowerCase().includes(s)
                );
                if (filtered.length === 0) {
                  return <div style={{ textAlign: 'center', color: '#999', padding: '2rem 0' }}>Aucun marché trouvé</div>;
                }
                return filtered.map(m => {
                  const etat = getEtat(m);
                  const badge = ETAT_BADGE[etat] || ETAT_BADGE['En cours'];
                  return (
                    <div key={m.reference_interne}
                      onClick={() => selectDuplicate(m)}
                      style={{
                        padding: '0.75rem 1rem', borderRadius: '6px', cursor: 'pointer',
                        border: '1px solid #ecf0f1', marginBottom: '0.5rem',
                        transition: 'all 0.15s', background: '#fff',
                      }}
                      onMouseEnter={e => { e.currentTarget.style.background = '#f0f3ff'; e.currentTarget.style.borderColor = '#3498db'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = '#fff'; e.currentTarget.style.borderColor = '#ecf0f1'; }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.25rem' }}>
                        <strong style={{ fontSize: '0.9rem' }}>{m.codification}</strong>
                        <span style={{ ...badge, padding: '0.15rem 0.5rem', borderRadius: '10px', fontSize: '0.75rem', fontWeight: 600 }}>{etat}</span>
                      </div>
                      <div style={{ fontSize: '0.85rem', color: '#2c3e50' }}>{m.intitule_marche || '—'}</div>
                      <div style={{ fontSize: '0.78rem', color: '#7f8c8d', marginTop: '0.2rem' }}>
                        {m.client_nom || '—'}{m.lot ? ` — ${m.lot}` : ''}{m.entreprise_nom ? ` (${m.entreprise_nom})` : ''}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default MarcheList;
